import { Component, ComponentClass } from './ComponentBase';

/**
 * Central registry for all components in the Entity Component System.
 * Validates serialization contract at registration time (boot/import time).
 *
 * Features:
 * - Dual lookup: by class name (ActorStats) or camelCase key (actorStats)
 * - Boot-time validation of required methods (equals, getTestInstances)
 * - Clear error messages for debugging
 * - Singleton pattern
 */
class ComponentRegistry {
  private components = new Map<string, ComponentClass>();
  private keyToClass = new Map<string, ComponentClass>();
  private errors: string[] = [];

  /**
   * Register a component class.
   * Validates that equals and getTestInstances are properly implemented.
   *
   * @param ComponentClass - The component class to register
   * @returns The same class (for chaining/decoration)
   * @throws Error if validation fails
   */
  register<T extends Component>(ComponentClass: ComponentClass<T>): ComponentClass<T> {
    const name = ComponentClass.name;

    // Validate: must have a name
    if (!name) {
      const error = `Component class must have a name (not anonymous)`;
      this.errors.push(error);
      throw new Error(`[ComponentRegistry] ${error}`);
    }

    // Validate: getTestInstances must be implemented
    if (typeof ComponentClass.getTestInstances !== 'function') {
      const error = `Component ${name} must implement static getTestInstances(): ${name}[]`;
      this.errors.push(error);
      throw new Error(`[ComponentRegistry] ${error}`);
    }

    // Validate: getTestInstances must not be the base class version (which throws)
    let testInstances: Component[];
    try {
      testInstances = ComponentClass.getTestInstances();
    } catch (e: any) {
      if (e.message && e.message.includes('not implemented')) {
        const error = `Component ${name} has not overridden getTestInstances - using base class version that throws`;
        this.errors.push(error);
        throw new Error(`[ComponentRegistry] ${error}`);
      }
      // Re-throw other errors - this is a real problem
      throw e;
    }

    // Validate: getTestInstances must return a non-empty array
    if (!Array.isArray(testInstances)) {
      const error = `Component ${name}.getTestInstances() must return an array, got ${typeof testInstances}`;
      this.errors.push(error);
      throw new Error(`[ComponentRegistry] ${error}`);
    }
    if (testInstances.length === 0) {
      const error = `Component ${name}.getTestInstances() must return a non-empty array`;
      this.errors.push(error);
      throw new Error(`[ComponentRegistry] ${error}`);
    }

    // Validate: equals must be implemented (not using base class version that throws)
    try {
      // Try calling equals on a test instance with itself
      const testInstance = testInstances[0];
      const result = testInstance.equals(testInstance);
      // If we get here, equals is implemented (should return true for self-equality)
      if (result !== true) {
        console.warn(`[ComponentRegistry] ${name}.equals() returned ${result} for self-equality (expected true)`);
      }
    } catch (e: any) {
      if (e.message && e.message.includes('not implemented')) {
        const error = `Component ${name} has not overridden equals() - using base class version that throws`;
        this.errors.push(error);
        throw new Error(`[ComponentRegistry] ${error}`);
      }
      // Re-throw other errors - this is a real problem
      throw e;
    }

    // Validate: must extend Component
    if (!(ComponentClass.prototype instanceof Component)) {
      const error = `Component ${name} must extend Component base class`;
      this.errors.push(error);
      throw new Error(`[ComponentRegistry] ${error}`);
    }

    // Generate camelCase key
    const key = this.makeKey(name);

    // Check for duplicates
    if (this.components.has(name)) {
      console.warn(`[ComponentRegistry] Component ${name} already registered - overwriting`);
    }
    if (this.keyToClass.has(key) && this.keyToClass.get(key) !== ComponentClass) {
      console.warn(`[ComponentRegistry] Key collision for "${key}" - overwriting`);
    }

    // Store both ways: by class name and by camelCase key
    this.components.set(name, ComponentClass);
    this.keyToClass.set(key, ComponentClass);

    //console.log(`[ComponentRegistry] Registered: ${name} (key: ${key})`);
    return ComponentClass;
  }

  /**
   * Get a component class by class name OR camelCase key.
   *
   * @param nameOrKey - The class name (ActorStats) or camelCase key (actorStats)
   * @returns The component class, or undefined if not found
   *
   * @example
   * ```typescript
   * const cls = componentRegistry.get('ActorStats');  // By class name
   * const cls = componentRegistry.get('actorStats');  // By camelCase key
   * ```
   */
  get(nameOrKey: string): ComponentClass | undefined {
    return this.components.get(nameOrKey) || this.keyToClass.get(nameOrKey);
  }

  /**
   * Get the database key for a component class (camelCase version of class name).
   *
   * @param ComponentClass - The component class
   * @returns The camelCase key used for storage
   *
   * @example
   * ```typescript
   * componentRegistry.getKey(ActorStats)  // Returns "actorStats"
   * ```
   */
  getKey(ComponentClass: ComponentClass): string {
    return this.makeKey(ComponentClass.name);
  }

  /**
   * Check if a component is registered.
   *
   * @param nameOrKey - The class name or camelCase key
   * @returns True if the component is registered
   */
  has(nameOrKey: string): boolean {
    return this.components.has(nameOrKey) || this.keyToClass.has(nameOrKey);
  }

  /**
   * Get all registered component class names.
   *
   * @returns Array of class names
   */
  getNames(): string[] {
    return Array.from(this.components.keys());
  }

  /**
   * Get all registered component keys (camelCase).
   *
   * @returns Array of camelCase keys
   */
  getKeys(): string[] {
    return Array.from(this.keyToClass.keys());
  }

  /**
   * Get validation errors (if any occurred during registration).
   *
   * @returns Array of error messages
   */
  getErrors(): string[] {
    return [...this.errors];
  }

  /**
   * Convert a class name to a camelCase key.
   *
   * @param className - The PascalCase class name
   * @returns The camelCase key
   * @private
   */
  private makeKey(className: string): string {
    return className.charAt(0).toLowerCase() + className.slice(1);
  }
}

// Singleton instance
export const componentRegistry = new ComponentRegistry();
